import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { AppProvider } from './context/AppContext';
import { ToastProvider } from './context/NotificationContext';
import { UserProvider, useUser } from './context/UserContext';
import { LoadingSpinner } from './components/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary';
import { ToastContainer } from './components/ToastContainer';
import { PageTransition } from './components/PageTransition';
import { MainLayout } from './components/layout/MainLayout';
import { lazyWithRetry } from './utils/lazyWithRetry';
import './utils/envValidation';

// Lazy load route pages
const WelcomePage = lazyWithRetry(() => import('./pages/WelcomePage').then(m => ({ default: m.WelcomePage })), 'welcome-page');
const WritingPage = lazyWithRetry(() => import('./pages/WritingPage').then(m => ({ default: m.WritingPage })), 'writing-page');
const ResultPage = lazyWithRetry(() => import('./pages/ResultPage').then(m => ({ default: m.ResultPage })), 'result-page');
const HistoryPage = lazyWithRetry(() => import('./pages/HistoryPage').then(m => ({ default: m.HistoryPage })), 'history-page');
const AnalyticsPage = lazyWithRetry(() => import('./pages/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })), 'analytics-page');
const SettingsPage = lazyWithRetry(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })), 'settings-page');
const ProfilePage = lazyWithRetry(() => import('./pages/ProfilePage').then(m => ({ default: m.default })), 'profile-page');
const DashboardPage = lazyWithRetry(() => import('./pages/DashboardPage').then(m => ({ default: m.default })), 'dashboard-page');
const ResultImprovePage = lazyWithRetry(() => import('./pages/ResultImprovePage').then(m => ({ default: m.default })), 'result-improve-page');
const AuthCallbackPage = lazyWithRetry(() => import('./pages/AuthCallbackPage').then(m => ({ default: m.AuthCallbackPage })), 'auth-callback-page');
const ActivateInvitePage = lazyWithRetry(() => import('./pages/ActivateInvitePage').then(m => ({ default: m.ActivateInvitePage })), 'activate-invite-page');
const ForgotPasswordPage = lazyWithRetry(() => import('./pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })), 'forgot-password-page');
const ResetPasswordPage = lazyWithRetry(() => import('./pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })), 'reset-password-page');

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoggedIn } = useUser();
  
  return isLoggedIn ? <>{children}</> : <Navigate to="/welcome" replace />;
};

const AppContent: React.FC = () => {
  return (
    <MainLayout>
      <Suspense 
        fallback={
          <div className="flex h-[50vh] items-center justify-center">
            <LoadingSpinner size="lg" text="Loading..." />
          </div>
        }
      >
        <PageTransition>
          <Routes>
            <Route path="/welcome" element={<WelcomePage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/auth/activate-invite" element={<ActivateInvitePage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/practice" element={<ProtectedRoute><WritingPage /></ProtectedRoute>} />
            <Route path="/practice/custom" element={<Navigate to="/practice?mode=custom&task=task2" replace />} />
            <Route path="/result" element={<ProtectedRoute><ResultPage /></ProtectedRoute>} />
            <Route path="/result/improve/:attemptId" element={<ProtectedRoute><ResultImprovePage /></ProtectedRoute>} />
            <Route path="/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/welcome" replace />} />
          </Routes>
        </PageTransition>
      </Suspense>
      <ToastContainer />
    </MainLayout>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <UserProvider>
          <AppProviderWrapper />
        </UserProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

const AppProviderWrapper: React.FC = () => {
  const { user, isLoggedIn } = useUser();
  
  return (
    <AppProvider user={user} isLoggedIn={isLoggedIn}>
      <ToastProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <AppContent />
        </Router>
      </ToastProvider>
    </AppProvider>
  );
};

export default App;
